import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import axios from 'axios';
import authService from '../services/authService';
import { UserProfile } from '../types/user';

interface AuthContextValue {
  token: string | null;
  role: string | null;
  roles: string[];
  permissions: string[];
  user: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string, tokenOverride?: string, roleOverride?: string) => Promise<void>;
  register: (email: string, password: string, alias: string) => Promise<void>;
  logout: () => void;
  refreshProfile: (tokenOverride?: string) => Promise<void>;
  totalScore: number;
  /**
   * Benutzerlevel (0..10). Wenn der Server ein explizites Feld `level` liefert, wird
   * dieses verwendet. Andernfalls wird aus dem kumulierten `totalScore` ein Level
   * abgeleitet (konfigurierbare Schwellen/Heuristik). Admins behalten vollen Zugriff.
   */
  userLevel: number;
  hasRole: (role: string) => boolean;
  hasRolePrefix: (prefix: string) => boolean;
  hasPermission: (permission: string) => boolean;
  canManageUsers: boolean;
  canManageTeams: boolean;
  canManageUpdates: boolean;
  canManageQuiz: boolean;
  canManageQuizQuestions: boolean;
  canManageFaq: boolean;
  canViewFaq: boolean;
  canAskFaqQuestion: boolean;
  canAnswerFaq: boolean;
  canUploadFaqImages: boolean;
  canManageDocuments: boolean;
  canViewDocuments: boolean;
  canEditDocuments: boolean;
  canDeleteDocuments: boolean;
  canManagePhonebook: boolean;
  canViewPhonebook: boolean;
  canEditPhonebook: boolean;
  canDeletePhonebook: boolean;
  canUploadPhonebookImages: boolean;
  canManageDashboard: boolean;
  canManageHighscore: boolean;
  canViewHighscorePublic: boolean;
  canViewHighscoreAggregate: boolean;
  canManageHighscorePrefs: boolean;
  canViewHighscoreAdminDetails: boolean;
  canManageHighscoreBars: boolean;
  canAccessAdmin: boolean;
  canViewRolesLegend: boolean;
  canEditRolesLegend: boolean;
  canManageThemeSettings: boolean;
  canEditContent: boolean;
  canEditTexts: boolean;
  canUploadImages: boolean;
  canEditTextsM1: boolean;
  canUploadImagesM1: boolean;
  canEditTextsM2: boolean;
  canUploadImagesM2: boolean;
  canEditTextsM3: boolean;
  canUploadImagesM3: boolean;
  canEditTextsM4: boolean;
  canUploadImagesM4: boolean;
  canEditTextsM5: boolean;
  canUploadImagesM5: boolean;
  canEditTextsM6: boolean;
  canUploadImagesM6: boolean;
  canUploadVideos: boolean;
  canUploadPdfs: boolean;
  canManageVorschlaege: boolean;
  canViewVorschlaege: boolean;
  canSubmitVorschlaege: boolean;
  canManageStepByStep: boolean;
  canEditStepByStep: boolean;
  canUploadStepByStepMedia: boolean;
  canManageStepByStepVisibility: boolean;
  canViewM10Datentypen: boolean;
  canEditM10DatentypenAccess: boolean;
  canViewM10Funktionen: boolean;
  canEditM10FunktionenAccess: boolean;
  canViewM10Fup: boolean;
  canEditM10FupAccess: boolean;
  canEditM10FupBlocks: boolean;
  canEditM10FupNetworks: boolean;
  canUseM10FupSimulation: boolean;
  canSaveM10FupProjects: boolean;
  canValidateM10FupTasks: boolean;
  canViewM10Cad: boolean;
  canEditM10CadAccess: boolean;
  canEditM10CadComponents: boolean;
  canEditM10CadConnections: boolean;
  canUseM10CadSimulation: boolean;
  canSaveM10CadProjects: boolean;
  canValidateM10CadTasks: boolean;
  canAnswerQuizzes: boolean;
  canManageProfile: boolean;
  canViewDipschalter: boolean;
  canViewTaupunkt: boolean;
  canViewVolumenstrom: boolean;
  canViewTemperaturen: boolean;
  canViewKabelberechnung: boolean;
  canSaveKabelberechnung: boolean;
  canViewLeistungsberechnung: boolean;
  canViewWiderstand: boolean;
  canViewSkalierer: boolean;
  canViewFarbCodex: boolean;
  canViewEnergieKosten: boolean;
  canViewEnergieRechner: boolean;
  canPlayCarRace: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readOAuthBootstrapParams(): { token: string | null; role: string | null; roles: string[]; email: string | null } {
  if (typeof window === 'undefined') {
    return { token: null, role: null, roles: [], email: null };
  }

  const merged = new URLSearchParams(window.location.search || '');
  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex >= 0) {
    const hashParams = new URLSearchParams(hash.slice(queryIndex + 1));
    hashParams.forEach((value, key) => {
      if (!merged.has(key)) merged.set(key, value);
    });
  }

  const token = merged.get('sso_token');
  const role = merged.get('sso_role');
  const rolesRaw = merged.get('sso_roles');
  const email = merged.get('sso_email');
  const roles = rolesRaw
    ? rolesRaw.split(',').map((r) => r.trim().toLowerCase()).filter((r) => r.length > 0)
    : [];

  return { token, role, roles, email };
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const bootstrap = readOAuthBootstrapParams();
  const [token, setToken] = useState<string | null>(() => authService.getToken() || bootstrap.token);
  const [role, setRole] = useState<string | null>(() => localStorage.getItem('auth_role') || bootstrap.role);
  const [roles, setRoles] = useState<string[]>(() => {
    const stored = (authService.getRoles() || []).map((r) => r.trim().toLowerCase()).filter((r) => r.length > 0);
    if (stored.length > 0) return stored;
    return bootstrap.roles;
  });
  const [permissions, setPermissions] = useState<string[]>([]);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const merged = new URLSearchParams(window.location.search || '');
    const hash = window.location.hash || '';
    const queryIndex = hash.indexOf('?');
    if (queryIndex >= 0) {
      const hashParams = new URLSearchParams(hash.slice(queryIndex + 1));
      hashParams.forEach((value, key) => {
        if (!merged.has(key)) merged.set(key, value);
      });
    }

    const oauthToken = merged.get('sso_token');
    const oauthRole = merged.get('sso_role') || 'user';
    const oauthRoles = merged.get('sso_roles');
    const oauthEmail = merged.get('sso_email');

    if (!oauthToken || !oauthEmail) return;

    authService.storeToken(oauthToken);
    authService.storeRole(oauthRole);
    setToken(oauthToken);
    setRole(oauthRole);

    if (oauthRoles) {
      const parsedRoles = oauthRoles
        .split(',')
        .map((r) => r.trim().toLowerCase())
        .filter((r) => r.length > 0);
      if (parsedRoles.length > 0) {
        authService.storeRoles(parsedRoles);
        setRoles(parsedRoles);
      }
    }

    try {
      window.history.replaceState(null, '', '/#/');
    } catch {}
  }, []);

  const computeTotalScore = (u: UserProfile | null): number => {
    if (!u) return 0;
    const keys = Object.keys(u).filter(k => k.startsWith('quiz_')) as (keyof UserProfile)[];
    return keys.reduce((sum, key) => {
      const raw = u[key];
      const num = typeof raw === 'string' ? parseInt(raw, 10) : (raw as number | undefined);
      return sum + (Number.isFinite(num) ? (num as number) : 0);
    }, 0);
  };

  const totalScore = computeTotalScore(user);

  const computeUserLevel = (u: UserProfile | null, score: number): number => {
    if (!u) return 0;
    // Wenn das Serverprofil ein level-Feld liefert, nutze es.
    const anyU = u as any;
    if (typeof anyU.level === 'number') return Math.max(0, Math.min(10, anyU.level));

    // Fallback: einfache Heuristik: level = floor(score / 20), begrenzt auf 0..10
    // (Anpassbar falls ihr andere Schwellen nutzen wollt.)
    const lvl = Math.floor(score / 20);
    return Math.max(0, Math.min(10, lvl));
  };

  const userLevel = computeUserLevel(user, totalScore);

  const normalizeRoles = useCallback((input: unknown): string[] => {
    if (!Array.isArray(input)) return [];
    return input
      .map((r) => String(r).trim().toLowerCase())
      .filter((r) => r.length > 0);
  }, []);

  const normalizePermissions = useCallback((input: unknown): string[] => {
    if (!Array.isArray(input)) return [];
    return input
      .map((p) => String(p).trim().toLowerCase())
      .filter((p) => p.length > 0);
  }, []);

  const MODULE_TEXT_PERMISSIONS = ['edit_texts_m1', 'edit_texts_m2', 'edit_texts_m3', 'edit_texts_m4', 'edit_texts_m5', 'edit_texts_m6'];
  const MODULE_IMAGE_PERMISSIONS = ['upload_images_m1', 'upload_images_m2', 'upload_images_m3', 'upload_images_m4', 'upload_images_m5', 'upload_images_m6'];
  const MODULE_CONTENT_PERMISSIONS = [...MODULE_TEXT_PERMISSIONS, ...MODULE_IMAGE_PERMISSIONS];

  const ROLE_PERMISSION_DEFAULTS: Record<string, string[]> = {
    admin: [
      'access_admin',
      'notify_new_user',
      'manage_profile',
      'manage_users',
      'manage_teams',
      'manage_updates',
      'manage_quiz',
      'manage_quiz_questions',
      'manage_faq',
      'view_faq',
      'ask_faq_question',
      'answer_faq',
      'upload_faq_images',
      'manage_documents',
      'view_documents',
      'edit_documents',
      'delete_documents',
      'manage_phonebook',
      'view_phonebook',
      'edit_phonebook',
      'delete_phonebook',
      'upload_phonebook_images',
      'manage_dashboard',
      'manage_highscore',
      'view_highscore_public',
      'view_highscore_aggregate',
      'manage_highscore_prefs',
      'view_highscore_admin_details',
      'manage_theme_settings',
      'edit_content',
      ...MODULE_CONTENT_PERMISSIONS,
      'edit_texts',
      'upload_images',
      'upload_videos',
      'upload_pdfs',
      'manage_vorschlaege',
      'view_vorschlaege',
      'submit_vorschlaege',
      'manage_stepbystep',
      'edit_stepbystep',
      'upload_stepbystep_media',
      'manage_stepbystep_visibility',
      'view_m10_datentypen',
      'edit_m10_datentypen_access',
      'view_m10_funktionen',
      'edit_m10_funktionen_access',
      'view_m10_fup',
      'edit_m10_fup_access',
      'edit_m10_fup_blocks',
      'edit_m10_fup_networks',
      'use_m10_fup_simulation',
      'save_m10_fup_projects',
      'validate_m10_fup_tasks',
      'view_m10_cad',
      'edit_m10_cad_access',
      'edit_m10_cad_components',
      'edit_m10_cad_connections',
      'use_m10_cad_simulation',
      'save_m10_cad_projects',
      'validate_m10_cad_tasks',
      'answer_quizzes',
      'view_roles_legend',
      'manage_roles_legend',
      'view_skalierer',
      'view_farbcodex',
      'view_temperaturen',
      'view_energiekosten',
      'view_energierechner',
      'play_car_race_game'
    ],
    light_admin: ['access_admin', 'manage_users', 'manage_teams', 'manage_updates', 'view_roles_legend'],
    user_admin: ['manage_users', 'view_roles_legend'],
    team_admin: ['manage_teams'],
    role_admin: ['manage_roles_legend', 'view_roles_legend'],
    updates_admin: ['manage_updates'],
    quiz_admin: ['manage_quiz_questions'],
    docs_admin: ['manage_documents', 'view_documents', 'edit_documents', 'delete_documents'],
    phonebook_admin: ['manage_phonebook', 'view_phonebook', 'edit_phonebook', 'delete_phonebook', 'upload_phonebook_images'],
    faq_admin: ['manage_faq', 'view_faq', 'ask_faq_question', 'answer_faq', 'upload_faq_images'],
    dashboard_admin: ['manage_dashboard'],
    highscore_admin: ['manage_highscore', 'view_highscore_public', 'view_highscore_aggregate', 'manage_highscore_prefs', 'view_highscore_admin_details'],
    content_admin: [...MODULE_CONTENT_PERMISSIONS, 'upload_videos', 'upload_pdfs', 'manage_stepbystep', 'edit_stepbystep', 'upload_stepbystep_media', 'manage_stepbystep_visibility', 'view_m10_datentypen', 'edit_m10_datentypen_access', 'view_m10_funktionen', 'edit_m10_funktionen_access', 'view_m10_fup', 'edit_m10_fup_access', 'edit_m10_fup_blocks', 'edit_m10_fup_networks', 'use_m10_fup_simulation', 'save_m10_fup_projects', 'validate_m10_fup_tasks', 'view_m10_cad', 'edit_m10_cad_access', 'edit_m10_cad_components', 'edit_m10_cad_connections', 'use_m10_cad_simulation', 'save_m10_cad_projects', 'validate_m10_cad_tasks', 'view_skalierer', 'view_farbcodex', 'view_temperaturen', 'view_energiekosten', 'view_energierechner'],
    editor: [...MODULE_CONTENT_PERMISSIONS, 'edit_stepbystep', 'upload_stepbystep_media', 'view_m10_datentypen', 'view_m10_funktionen', 'view_m10_fup', 'edit_m10_fup_blocks', 'edit_m10_fup_networks', 'use_m10_fup_simulation', 'save_m10_fup_projects', 'validate_m10_fup_tasks', 'view_m10_cad', 'edit_m10_cad_components', 'edit_m10_cad_connections', 'use_m10_cad_simulation', 'save_m10_cad_projects', 'validate_m10_cad_tasks', 'view_skalierer', 'view_farbcodex', 'view_temperaturen', 'view_energiekosten', 'view_energierechner'],
    creator: [...MODULE_CONTENT_PERMISSIONS, 'upload_videos', 'upload_pdfs', 'edit_stepbystep', 'upload_stepbystep_media', 'view_m10_datentypen', 'view_m10_funktionen', 'view_m10_fup', 'edit_m10_fup_blocks', 'edit_m10_fup_networks', 'use_m10_fup_simulation', 'save_m10_fup_projects', 'validate_m10_fup_tasks', 'view_m10_cad', 'edit_m10_cad_components', 'edit_m10_cad_connections', 'use_m10_cad_simulation', 'save_m10_cad_projects', 'validate_m10_cad_tasks', 'view_skalierer', 'view_farbcodex', 'view_temperaturen', 'view_energiekosten', 'view_energierechner'],
    user: ['manage_profile', 'answer_quizzes', 'view_documents', 'view_highscore_public', 'view_highscore_aggregate', 'view_phonebook', 'view_faq', 'ask_faq_question', 'upload_faq_images', 'view_vorschlaege', 'submit_vorschlaege', 'view_skalierer', 'view_farbcodex', 'view_temperaturen', 'view_energiekosten', 'view_energierechner']
  };

  const getDefaultPermissionsForRole = useCallback((key: string): string[] => {
    const normalized = key.trim().toLowerCase();
    if (!normalized) return [];
    if (ROLE_PERMISSION_DEFAULTS[normalized]) return ROLE_PERMISSION_DEFAULTS[normalized];
    if (normalized.startsWith('editor_')) return [...MODULE_CONTENT_PERMISSIONS];
    if (normalized.startsWith('creator_')) return [...MODULE_CONTENT_PERMISSIONS, 'upload_videos', 'upload_pdfs'];
    return [];
  }, [MODULE_CONTENT_PERMISSIONS, ROLE_PERMISSION_DEFAULTS]);

  const derivePermissionsFromRoles = useCallback((roleList: string[], legendRoles?: Array<{ key?: string; permissions?: string[] }>): string[] => {
    const normalizedRoles = roleList.map((r) => r.trim().toLowerCase()).filter(Boolean);
    const merged = new Set<string>();
    const legendMap = new Map<string, string[]>();
    if (Array.isArray(legendRoles)) {
      legendRoles.forEach((roleEntry) => {
        const key = String(roleEntry?.key || '').trim().toLowerCase();
        if (!key) return;
        legendMap.set(key, normalizePermissions(roleEntry.permissions));
      });
    }

    normalizedRoles.forEach((r) => {
      const perms = legendMap.get(r) || getDefaultPermissionsForRole(r);
      perms.forEach((p) => merged.add(p));
    });

    return Array.from(merged);
  }, [getDefaultPermissionsForRole, normalizePermissions]);

  const refreshProfile = useCallback(async (tokenOverride?: string) => {
    const activeToken = tokenOverride || token;
    if (!activeToken) return;
    try {
      setLoading(true);
      const profile = await authService.getProfile(activeToken);
      setUser(profile);
      const resolvedRoles = normalizeRoles(profile?.roles)
        .concat(normalizeRoles(profile?.role ? [profile.role] : []))
        .concat(roles)
        .concat(role ? [role] : [])
        .filter((r, idx, arr) => r.length > 0 && arr.indexOf(r) === idx);
      const finalRoles = resolvedRoles.length > 0 ? resolvedRoles : ['user'];
      if (finalRoles.length > 0) {
        setRoles(finalRoles);
        try { authService.storeRoles(finalRoles); } catch {}
      }
      if (finalRoles[0]) {
        setRole(finalRoles[0]);
        try { authService.storeRole(finalRoles[0]); } catch {}
      }
      // Stelle sicher, dass die Rolle im Frontend der Serverrolle entspricht
      if (profile?.role && profile.role !== role) {
        setRole(profile.role);
        try { authService.storeRole(profile.role); } catch {}
      }
      if (Array.isArray(profile?.roles)) {
        const normalized = normalizeRoles(profile.roles);
        setRoles(normalized);
        try { authService.storeRoles(normalized); } catch {}
      } else if (profile?.role) {
        const fallback = normalizeRoles([profile.role]);
        if (fallback.length > 0) {
          setRoles(fallback);
          try { authService.storeRoles(fallback); } catch {}
        }
      }
      if (Array.isArray(profile?.permissions) && profile.permissions.length > 0) {
        const normalizedPermissions = normalizePermissions(profile.permissions);
        setPermissions(normalizedPermissions);
      } else {
        let legendRoles: Array<{ key?: string; permissions?: string[] }> | undefined;
        try {
          const legendResult = await authService.getRolesLegend(activeToken);
          if (legendResult?.success && Array.isArray(legendResult?.legend?.roles)) {
            legendRoles = legendResult.legend.roles;
          }
        } catch {}
        const derived = derivePermissionsFromRoles(finalRoles, legendRoles);
        setPermissions(derived);
      }
    } catch (e) {
      // Optional: Fehlerbehandlung / automatischer Logout bei 401
    } finally {
      setLoading(false);
    }
  }, [token, role, roles, normalizePermissions, normalizeRoles, derivePermissionsFromRoles]);

  const login = async (email: string, password: string, tokenOverride?: string, roleOverride?: string) => {
    setLoading(true);
    try {
      if (tokenOverride) {
        authService.storeToken(tokenOverride);
        if (roleOverride) authService.storeRole(roleOverride);
        setToken(tokenOverride);
        setRole(roleOverride || null);
        const storedRoles = normalizeRoles(authService.getRoles() || []);
        if (storedRoles.length > 0) {
          setRoles(storedRoles);
          try { authService.storeRoles(storedRoles); } catch {}
        } else if (roleOverride) {
          const fallbackRoles = normalizeRoles([roleOverride]);
          if (fallbackRoles.length > 0) {
            setRoles(fallbackRoles);
            try { authService.storeRoles(fallbackRoles); } catch {}
          }
        }
        await refreshProfile(tokenOverride);
        return;
      }

      const data = await authService.login(email, password);
      if (data.token) {
        authService.storeToken(data.token);
        if (data.role) authService.storeRole(data.role);
        if (Array.isArray(data.roles)) {
          const normalized = normalizeRoles(data.roles);
          authService.storeRoles(normalized);
          setRoles(normalized);
        } else if (data.role) {
          const fallback = normalizeRoles([data.role]);
          if (fallback.length > 0) {
            authService.storeRoles(fallback);
            setRoles(fallback);
          }
        }
        setToken(data.token);
        setRole(data.role || null);
        await refreshProfile(data.token);
      } else {
        throw new Error('Kein Token erhalten.');
      }
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, alias: string) => {
    setLoading(true);
    try {
      await authService.register(email, password, alias);
      // Nach Registrierung KEIN automatischer Login (wie Android) – Benutzer muss erst aktiviert werden.
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.removeToken();
    setToken(null);
    setRole(null);
    setRoles([]);
    authService.removeRoles();
    setUser(null);
  };

  const forceLogout = useCallback((message?: string) => {
    logout();
    const text = (message && message.trim().length > 0)
      ? message
      : 'Ups, es ist ein Fehler unterlaufen. Bitte erneut anmelden. (E-000)';
    if (typeof window !== 'undefined') {
      const reason = encodeURIComponent(text);
      window.location.replace(`/#/login-error?reason=${reason}`);
    }
  }, [logout]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleForceLogout = (payload?: any) => {
      if (payload?.forceLogout) {
        forceLogout(payload?.error);
      }
    };

    const axiosInterceptor = axios.interceptors.response.use(
      (response) => {
        if (response?.data?.forceLogout) {
          handleForceLogout(response.data);
        }
        return response;
      },
      (error) => {
        handleForceLogout(error?.response?.data);
        return Promise.reject(error);
      }
    );

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401) {
        let payload: any = null;
        try {
          payload = await response.clone().json();
        } catch {
          payload = null;
        }
        handleForceLogout(payload);
      }
      return response;
    };

    return () => {
      axios.interceptors.response.eject(axiosInterceptor);
      window.fetch = originalFetch;
    };
  }, [forceLogout]);

  useEffect(() => {
    if (!token || user) {
      return;
    }

    refreshProfile();
  }, [token, user, refreshProfile]);

  const hasRole = useCallback(
    (targetRole: string) => {
      const normalized = targetRole.trim().toLowerCase();
      if (!normalized) return false;
      if (roles.includes(normalized)) return true;
      if (role && role.toLowerCase() === normalized) return true;
      return false;
    },
    [roles, role]
  );

  const hasRolePrefix = useCallback(
    (prefix: string) => {
      const normalized = prefix.trim().toLowerCase();
      if (!normalized) return false;
      return roles.some((r) => r.startsWith(normalized));
    },
    [roles]
  );

  const hasPermission = useCallback(
    (permission: string) => {
      const normalized = permission.trim().toLowerCase();
      if (!normalized) return false;
      if (permissions.length > 0) return permissions.includes(normalized);
      return false;
    },
    [permissions]
  );

  const canManageUsers = hasPermission('manage_users');
  const canManageTeams = hasPermission('manage_teams');
  const canManageUpdates = hasPermission('manage_updates');
  const canManageQuiz = hasPermission('manage_quiz');
  const canManageQuizQuestions = hasPermission('manage_quiz_questions') || hasPermission('manage_quiz');
  const canManageFaq = hasPermission('manage_faq');
  const canViewFaq = hasPermission('view_faq') || canManageFaq;
  const canAskFaqQuestion = hasPermission('ask_faq_question') || canManageFaq;
  const canAnswerFaq = hasPermission('answer_faq') || canManageFaq;
  const canUploadFaqImages = hasPermission('upload_faq_images') || canManageFaq;
  const canManageDocuments = hasPermission('manage_documents');
  const canViewDocuments = hasPermission('view_documents') || hasPermission('manage_documents');
  const canEditDocuments = hasPermission('edit_documents') || canManageDocuments;
  const canDeleteDocuments = hasPermission('delete_documents') || canManageDocuments;
  const canManagePhonebook = hasPermission('manage_phonebook');
  const canViewPhonebook = hasPermission('view_phonebook') || canManagePhonebook;
  const canEditPhonebook = hasPermission('edit_phonebook') || canManagePhonebook;
  const canDeletePhonebook = hasPermission('delete_phonebook') || canManagePhonebook;
  const canUploadPhonebookImages = hasPermission('upload_phonebook_images') || canManagePhonebook;
  const canManageDashboard = hasPermission('manage_dashboard');
  const canManageHighscore = hasPermission('manage_highscore');
  const canViewHighscorePublic = hasPermission('view_highscore_public') || canManageHighscore;
  const canViewHighscoreAggregate = hasPermission('view_highscore_aggregate') || canManageHighscore;
  const canManageHighscorePrefs = hasPermission('manage_highscore_prefs') || canManageHighscore;
  const canViewHighscoreAdminDetails = hasPermission('view_highscore_admin_details') || canManageHighscore;
  const canManageHighscoreBars = hasPermission('manage_highscore_bars') || canManageHighscore;
  const canAccessAdmin = hasPermission('access_admin');
  const canViewRolesLegend = hasPermission('view_roles_legend') || hasPermission('manage_roles_legend');
  const canEditRolesLegend = hasPermission('manage_roles_legend');
  const canManageThemeSettings = hasPermission('manage_theme_settings');
  const canEditTextsLegacy = hasPermission('edit_texts') || hasPermission('edit_content');
  const canUploadImagesLegacy = hasPermission('upload_images') || hasPermission('edit_content');
  const canEditTextsAnyModule =
    hasPermission('edit_texts_m1') ||
    hasPermission('edit_texts_m2') ||
    hasPermission('edit_texts_m3') ||
    hasPermission('edit_texts_m4') ||
    hasPermission('edit_texts_m5') ||
    hasPermission('edit_texts_m6');
  const canUploadImagesAnyModule =
    hasPermission('upload_images_m1') ||
    hasPermission('upload_images_m2') ||
    hasPermission('upload_images_m3') ||
    hasPermission('upload_images_m4') ||
    hasPermission('upload_images_m5') ||
    hasPermission('upload_images_m6');
  const canEditTexts = canEditTextsLegacy;
  const canUploadImages = canUploadImagesLegacy || canUploadImagesAnyModule;
  const canEditTextsM1 = hasPermission('edit_texts_m1') || canEditTextsLegacy;
  const canUploadImagesM1 = hasPermission('upload_images_m1') || canUploadImagesLegacy;
  const canEditTextsM2 = hasPermission('edit_texts_m2') || canEditTextsLegacy;
  const canUploadImagesM2 = hasPermission('upload_images_m2') || canUploadImagesLegacy;
  const canEditTextsM3 = hasPermission('edit_texts_m3') || canEditTextsLegacy;
  const canUploadImagesM3 = hasPermission('upload_images_m3') || canUploadImagesLegacy;
  const canEditTextsM4 = hasPermission('edit_texts_m4') || canEditTextsLegacy;
  const canUploadImagesM4 = hasPermission('upload_images_m4') || canUploadImagesLegacy;
  const canEditTextsM5 = hasPermission('edit_texts_m5') || canEditTextsLegacy;
  const canUploadImagesM5 = hasPermission('upload_images_m5') || canUploadImagesLegacy;
  const canEditTextsM6 = hasPermission('edit_texts_m6') || canEditTextsLegacy;
  const canUploadImagesM6 = hasPermission('upload_images_m6') || canUploadImagesLegacy;
  const canUploadVideos = hasPermission('upload_videos') || hasPermission('edit_content');
  const canUploadPdfs = hasPermission('upload_pdfs') || hasPermission('edit_content');
  const canManageVorschlaege = hasPermission('manage_vorschlaege') || hasPermission('edit_content');
  const canViewVorschlaege = hasPermission('view_vorschlaege') || canManageVorschlaege;
  const canSubmitVorschlaege = hasPermission('submit_vorschlaege') || canManageVorschlaege;
  const canManageStepByStep = hasPermission('manage_stepbystep') || hasPermission('edit_content');
  const canEditStepByStep = hasPermission('edit_stepbystep') || canManageStepByStep;
  const canUploadStepByStepMedia = hasPermission('upload_stepbystep_media') || canManageStepByStep;
  const canManageStepByStepVisibility = hasPermission('manage_stepbystep_visibility') || canManageStepByStep;
  // Datentypen-Seite (m10_u1_x1)
  const canViewM10Datentypen = hasPermission('view_m10_datentypen') || hasPermission('edit_content');
  const canEditM10DatentypenAccess = hasPermission('edit_m10_datentypen_access') || hasPermission('edit_content');
  // Funktionen-Seite (m10_u1_x2)
  const canViewM10Funktionen = hasPermission('view_m10_funktionen') || hasPermission('edit_content');
  const canEditM10FunktionenAccess = hasPermission('edit_m10_funktionen_access') || hasPermission('edit_content');
  // FUP-Editor (m10_u2_x1)
  const canViewM10Fup = hasPermission('view_m10_fup') || hasPermission('edit_content');
  const canEditM10FupAccess = hasPermission('edit_m10_fup_access') || hasPermission('edit_content');
  const canEditM10FupBlocks = hasPermission('edit_m10_fup_blocks') || hasPermission('edit_content');
  const canEditM10FupNetworks = hasPermission('edit_m10_fup_networks') || hasPermission('edit_content');
  const canUseM10FupSimulation = hasPermission('use_m10_fup_simulation') || hasPermission('edit_content');
  const canSaveM10FupProjects = hasPermission('save_m10_fup_projects') || hasPermission('edit_content');
  const canValidateM10FupTasks = hasPermission('validate_m10_fup_tasks') || hasPermission('edit_content');
  // CAD-Schaltungseditor (m10_u2_x2)
  const canViewM10Cad = hasPermission('view_m10_cad') || hasPermission('edit_content');
  const canEditM10CadAccess = hasPermission('edit_m10_cad_access') || hasPermission('edit_content');
  const canEditM10CadComponents = hasPermission('edit_m10_cad_components') || hasPermission('edit_content');
  const canEditM10CadConnections = hasPermission('edit_m10_cad_connections') || hasPermission('edit_content');
  const canUseM10CadSimulation = hasPermission('use_m10_cad_simulation') || hasPermission('edit_content');
  const canSaveM10CadProjects = hasPermission('save_m10_cad_projects') || hasPermission('edit_content');
  const canValidateM10CadTasks = hasPermission('validate_m10_cad_tasks') || hasPermission('edit_content');
  const canEditContent =
    hasPermission('edit_content') ||
    canEditTexts ||
    canEditTextsAnyModule ||
    canUploadImages ||
    canUploadImagesAnyModule ||
    canUploadVideos ||
    canUploadPdfs;
  const canAnswerQuizzes = hasPermission('answer_quizzes') || canManageQuizQuestions || hasPermission('manage_quiz');
  const canManageProfile = hasPermission('manage_profile');
  const canViewDipschalter = hasPermission('view_dipschalter');
  const canViewTaupunkt = hasPermission('view_taupunkt');
  const canViewVolumenstrom = hasPermission('view_volumenstrom');
  const canViewTemperaturen = hasPermission('view_temperaturen');
  const canViewKabelberechnung = hasPermission('view_kabelberechnung');
  const canSaveKabelberechnung = hasPermission('save_kabelberechnung');
  const canViewLeistungsberechnung = hasPermission('view_leistungsberechnung');
  const canViewWiderstand = hasPermission('view_widerstand');
  const canViewSkalierer = hasPermission('view_skalierer');
  const canViewFarbCodex = hasPermission('view_farbcodex');
  const canViewEnergieKosten = hasPermission('view_energiekosten');
  const canViewEnergieRechner = hasPermission('view_energierechner');
  const canPlayCarRace = hasPermission('play_car_race_game');

  const value: AuthContextValue = {
    token,
    role,
    roles,
    permissions,
    user,
    loading,
    login,
    register,
    logout,
    refreshProfile,
    totalScore,
    userLevel,
    hasRole,
    hasRolePrefix,
    hasPermission,
    canManageUsers,
    canManageTeams,
    canManageUpdates,
    canManageQuiz,
    canManageQuizQuestions,
    canManageFaq,
    canViewFaq,
    canAskFaqQuestion,
    canAnswerFaq,
    canUploadFaqImages,
    canManageDocuments,
    canViewDocuments,
    canEditDocuments,
    canDeleteDocuments,
    canManagePhonebook,
    canViewPhonebook,
    canEditPhonebook,
    canDeletePhonebook,
    canUploadPhonebookImages,
    canManageDashboard,
    canManageHighscore,
    canViewHighscorePublic,
    canViewHighscoreAggregate,
    canManageHighscorePrefs,
    canViewHighscoreAdminDetails,
    canManageHighscoreBars,
    canAccessAdmin,
    canViewRolesLegend,
    canEditRolesLegend,
    canManageThemeSettings,
    canEditContent,
    canEditTexts,
    canUploadImages,
    canEditTextsM1,
    canUploadImagesM1,
    canEditTextsM2,
    canUploadImagesM2,
    canEditTextsM3,
    canUploadImagesM3,
    canEditTextsM4,
    canUploadImagesM4,
    canEditTextsM5,
    canUploadImagesM5,
    canEditTextsM6,
    canUploadImagesM6,
    canUploadVideos,
    canUploadPdfs,
    canManageVorschlaege,
    canViewVorschlaege,
    canSubmitVorschlaege,
    canManageStepByStep,
    canEditStepByStep,
    canUploadStepByStepMedia,
    canManageStepByStepVisibility,
    canViewM10Datentypen,
    canEditM10DatentypenAccess,
    canViewM10Funktionen,
    canEditM10FunktionenAccess,
    canViewM10Fup,
    canEditM10FupAccess,
    canEditM10FupBlocks,
    canEditM10FupNetworks,
    canUseM10FupSimulation,
    canSaveM10FupProjects,
    canValidateM10FupTasks,
    canViewM10Cad,
    canEditM10CadAccess,
    canEditM10CadComponents,
    canEditM10CadConnections,
    canUseM10CadSimulation,
    canSaveM10CadProjects,
    canValidateM10CadTasks,
    canAnswerQuizzes,
    canManageProfile,
    canViewDipschalter,
    canViewTaupunkt,
    canViewVolumenstrom,
    canViewTemperaturen,
    canViewKabelberechnung,
    canSaveKabelberechnung,
    canViewLeistungsberechnung,
    canViewWiderstand,
    canViewSkalierer,
    canViewFarbCodex,
    canViewEnergieKosten,
    canViewEnergieRechner,
    canPlayCarRace,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden.');
  return ctx;
};
